import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaFilter,
  FaSpinner,
  FaToggleOn,
  FaToggleOff,
  FaFileExcel,
  FaFilePdf
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import courseMasterService from '../services/courseMasterService.jsx';

// Import jsPDF and autoTable correctly
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const MasterDetail = () => {
  const { masterName } = useParams();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [activeOnly, setActiveOnly] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    courseName: '',
    category: '',
    description: '',
    active: true,
    duration: '',
    priceINR: 0,
    priceUSD: 0,
    halfINR: 0,
    halfUSD: 0,
    imageUrl: '',
    enrolledCount: 0
  });

  const recordsPerPage = 10;

  const fetchAllCourses = async () => {
    setLoading(true);
    try {
      const data = await courseMasterService.getAllCourses();
      const coursesList = data.courses || [];
      setRecords(coursesList);
      setCategories([...new Set(coursesList.map(c => c.category).filter(Boolean))]);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error(error?.response?.data?.error || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    try {
      if (filteredRecords.length === 0) {
        toast.warning('No records to export');
        return;
      }

      const exportData = filteredRecords.map(record => ({
        'Course ID': record.courseId || '',
        'Course Name': record.courseName || '',
        'Category': record.category || '',
        'Description': (record.description || '').substring(0, 200),
        'Status': record.active ? 'Active' : 'Inactive',
        'Duration': record.duration || 'N/A',
        'Price (INR)': record.priceINR || 0,
        'Price (USD)': record.priceUSD || 0,
        'Half Payment (INR)': record.halfINR || 0,
        'Half Payment (USD)': record.halfUSD || 0,
        'Enrolled Count': record.enrolledCount || 0
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Courses');
      XLSX.writeFile(wb, `courses_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export to Excel');
    }
  };

  // Export to PDF - Fixed version
  const exportToPDF = () => {
    try {
      if (filteredRecords.length === 0) {
        toast.warning('No records to export');
        return;
      }

      // Create new PDF document in landscape mode
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      // Add header
      doc.setFontSize(18);
      doc.setTextColor(41, 128, 185);
      doc.text('Courses Report', 14, 15);
      
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 25);
      doc.text(`Total Records: ${filteredRecords.length}`, 14, 32);
      
      // Prepare table data
      const tableData = filteredRecords.map(record => [
        (record.courseId || '').substring(0, 12),
        (record.courseName || '').substring(0, 25),
        (record.category || '').substring(0, 20),
        ((record.description || '').substring(0, 35) + ((record.description || '').length > 35 ? '...' : '')),
        record.active ? 'Active' : 'Inactive',
        record.duration || 'N/A',
        `₹${record.priceINR || 0}`,
        `$${record.priceUSD || 0}`,
        record.enrolledCount || 0
      ]);

      // Add table using autoTable
      autoTable(doc, {
        head: [['Course ID', 'Course Name', 'Category', 'Description', 'Status', 'Duration', 'Price (INR)', 'Price (USD)', 'Enrolled']],
        body: tableData,
        startY: 40,
        margin: { left: 10, right: 10 },
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: 'linebreak',
          lineColor: [200, 200, 200],
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'center',
        },
        bodyStyles: {
          valign: 'middle',
        },
        alternateRowStyles: {
          fillColor: [240, 248, 255],
        },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 35 },
          2: { cellWidth: 25 },
          3: { cellWidth: 40 },
          4: { cellWidth: 18 },
          5: { cellWidth: 18 },
          6: { cellWidth: 20 },
          7: { cellWidth: 20 },
          8: { cellWidth: 15 }
        },
        theme: 'striped',
        pageBreak: 'auto',
        showHead: 'everyPage',
      });

      // Add footer with page numbers
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `Page ${i} of ${pageCount}`,
          doc.internal.pageSize.getWidth() / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }

      // Save the PDF
      doc.save(`courses_report_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF file downloaded successfully!');
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error('Failed to export to PDF: ' + error.message);
    }
  };

  // Fetch courses if CourseMaster, otherwise generate sample data
  useEffect(() => {
    if (masterName === 'CourseMaster') {
      fetchAllCourses();
      return;
    }

    const sampleRecords = [];
    for (let i = 1; i <= 25; i++) {
      sampleRecords.push({
        id: i,
        name: `${masterName.replace('Master', '')} Record ${i}`,
        description: `This is a sample description for ${masterName.replace('Master', '').toLowerCase()} record ${i}. It contains relevant information about this data entry.`,
        status: i % 3 === 0 ? 'Inactive' : 'Active',
        createdAt: new Date(2024, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString().split('T')[0]
      });
    }
    setRecords(sampleRecords);
  }, [masterName]);

  // Filter records based on search and status
  useEffect(() => {
    let filtered = records;

    if (masterName === 'CourseMaster') {
      filtered = records.filter(record => {
        const matchesSearch = (record.courseName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                            (record.category?.toLowerCase() || '').includes(searchTerm.toLowerCase());
        const matchesCategory = categoryFilter === 'All' || record.category === categoryFilter;
        const matchesActive = !activeOnly || record.active === true;
        return matchesSearch && matchesCategory && matchesActive;
      });
    } else {
      filtered = records.filter(record =>
        (record.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (record.description?.toLowerCase() || '').includes(searchTerm.toLowerCase())
      );

      if (statusFilter !== 'All') {
        filtered = filtered.filter(record => record.status === statusFilter);
      }
    }

    setFilteredRecords(filtered);
    setCurrentPage(1);
  }, [records, searchTerm, statusFilter, categoryFilter, activeOnly, masterName]);

  // Pagination
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentRecords = filteredRecords.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleAddNew = () => {
    setEditingRecord(null);
    if (masterName === 'CourseMaster') {
      setFormData({
        courseName: '',
        category: '',
        description: '',
        active: true,
        duration: '',
        priceINR: 0,
        priceUSD: 0,
        halfINR: 0,
        halfUSD: 0,
        imageUrl: '',
        enrolledCount: 0
      });
    } else {
      setFormData({
        name: '',
        description: '',
        status: 'Active'
      });
    }
    setIsModalOpen(true);
  };

  const handleView = (record) => {
    setViewingRecord(record);
    setIsViewModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    if (masterName === 'CourseMaster') {
      setFormData({
        courseName: record.courseName || '',
        category: record.category || '',
        description: record.description || '',
        active: record.active ?? true,
        duration: record.duration || '',
        priceINR: record.priceINR || 0,
        priceUSD: record.priceUSD || 0,
        halfINR: record.halfINR || 0,
        halfUSD: record.halfUSD || 0,
        imageUrl: record.imageUrl || '',
        enrolledCount: record.enrolledCount || 0
      });
    } else {
      setFormData({
        name: record.name || '',
        description: record.description || '',
        status: record.status || 'Active'
      });
    }
    setIsModalOpen(true);
  };

  const handleDelete = (record) => {
    const recordName = masterName === 'CourseMaster' ? record.courseName : record.name;
    const courseId = record.courseId || record.id;

    Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete "${recordName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          if (masterName === 'CourseMaster') {
            await courseMasterService.deleteCourse(courseId);
            toast.success('Course deleted successfully');
            await fetchAllCourses();
          } else {
            setRecords(records.filter(r => r.id !== record.id));
          }
          Swal.fire('Deleted!', 'Record has been deleted.', 'success');
        } catch (error) {
          console.error('Error deleting record:', error);
          toast.error(error?.response?.data?.error || error?.response?.data?.message || 'Failed to delete record');
          Swal.fire('Error!', error?.response?.data?.error || error?.response?.data?.message || 'Failed to delete record.', 'error');
        }
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (masterName === 'CourseMaster') {
        if (editingRecord) {
          const updateData = {
            courseId: editingRecord.courseId || editingRecord.id,
            courseName: formData.courseName,
            category: formData.category,
            description: formData.description,
            active: formData.active,
            duration: formData.duration,
            priceINR: formData.priceINR,
            priceUSD: formData.priceUSD,
            halfINR: formData.halfINR,
            halfUSD: formData.halfUSD,
            imageUrl: formData.imageUrl,
            enrolledCount: formData.enrolledCount
          };

          const response = await courseMasterService.updateCourse(updateData);
          if (response.success) {
            toast.success('Course updated successfully');
            await fetchAllCourses();
          } else {
            toast.error(response.message || 'Failed to update course');
          }
        } else {
          const response = await courseMasterService.createCourse(formData);
          if (response.success) {
            toast.success('Course created successfully');
            await fetchAllCourses();
          } else {
            toast.error(response.message || 'Failed to create course');
          }
        }
      } else {
        if (editingRecord) {
          setRecords(records.map(record =>
            record.id === editingRecord.id
              ? { ...record, ...formData }
              : record
          ));
          toast.success('Record updated successfully');
        } else {
          const newRecord = {
            id: Math.max(...records.map(r => r.id), 0) + 1,
            ...formData,
            createdAt: new Date().toISOString().split('T')[0]
          };
          setRecords([...records, newRecord]);
          toast.success('Record added successfully');
        }
      }

      setIsModalOpen(false);
    } catch (error) {
      console.error('Error submitting form:', error);
      const message = error?.response?.data?.error || error?.response?.data?.message || (editingRecord ? 'Failed to update record' : 'Failed to create record');
      toast.error(message);
    }
  };

  const handleToggleStatus = async (record) => {
    const courseId = record.courseId || record.id;

    try {
      if (masterName === 'CourseMaster') {
        if (record.active) {
          await courseMasterService.deactivateCourse(courseId);
          toast.success(`${record.courseName} deactivated`);
        } else {
          await courseMasterService.activateCourse(courseId);
          toast.success(`${record.courseName} activated`);
        }
        await fetchAllCourses();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      const message = error?.response?.data?.error || error?.response?.data?.message || 'Failed to update status';
      toast.error(message);
    }
  };

  const getStatusBadge = (status) => {
    return status === 'Active' || status === true
      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
  };

  return (
    <div className="p-6">
      {loading && (
        <div className="flex items-center justify-center py-12">
          <FaSpinner className="text-4xl text-blue-600 animate-spin" />
          <span className="ml-3 text-lg text-gray-600 dark:text-gray-400">Loading data...</span>
        </div>
      )}

      {!loading && (
        <>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
            <div className="mb-4 sm:mb-0">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {masterName}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Manage {masterName.replace('Master', '').toLowerCase()} records
              </p>
            </div>
            <div className="flex gap-2">
              {masterName === 'CourseMaster' && (
                <>
                  <button
                    onClick={exportToExcel}
                    className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center"
                    title="Export to Excel"
                  >
                    <FaFileExcel className="mr-2" />
                    Export Excel
                  </button>
                  <button
                    onClick={exportToPDF}
                    className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center"
                    title="Export to PDF"
                  >
                    <FaFilePdf className="mr-2" />
                    Export PDF
                  </button>
                </>
              )}
              <button
                onClick={handleAddNew}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center"
              >
                <FaPlus className="mr-2" />
                Add New
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search records..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>

            {masterName === 'CourseMaster' ? (
              <>
                <div className="relative">
                  <FaFilter className="absolute left-3 top-3 text-gray-400" />
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="All">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={() => setActiveOnly(!activeOnly)}
                  className={`px-4 py-2 rounded-md font-medium flex items-center transition-colors ${
                    activeOnly
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white'
                  }`}
                >
                  {activeOnly ? <FaToggleOn className="mr-2" /> : <FaToggleOff className="mr-2" />}
                  Active Only
                </button>
              </>
            ) : (
              <div className="relative">
                <FaFilter className="absolute left-3 top-3 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="All">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            )}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      ID
                    </th>
                    {masterName === 'CourseMaster' ? (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Course Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Status
                        </th>
                      </>
                    ) : (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Created Date
                        </th>
                      </>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {currentRecords.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                        No records found
                      </td>
                    </tr>
                  ) : (
                    currentRecords.map((record) => (
                      <tr key={record.courseId || record.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {masterName === 'CourseMaster' ? (record.courseId || '').substring(0, 15) : record.id}
                        </td>
                        {masterName === 'CourseMaster' ? (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {record.courseName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                              {record.category}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-300 max-w-xs truncate">
                              {record.description}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={() => handleToggleStatus(record)}
                                className="inline-flex items-center"
                                title={record.active ? 'Click to deactivate' : 'Click to activate'}
                              >
                                {record.active ? (
                                  <FaToggleOn className="text-2xl text-green-500 hover:text-green-600" />
                                ) : (
                                  <FaToggleOff className="text-2xl text-red-500 hover:text-red-600" />
                                )}
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {record.name}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-300 max-w-xs truncate">
                              {record.description}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(record.status)}`}>
                                {record.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                              {record.createdAt}
                            </td>
                          </>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleView(record)}
                            className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 mr-3"
                            title="View details"
                          >
                            <FaEye />
                          </button>
                          <button
                            onClick={() => handleEdit(record)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDelete(record)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredRecords.length)} of {filteredRecords.length} records
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  <FaChevronLeft />
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      page === currentPage
                        ? 'text-blue-600 bg-blue-50 border border-blue-500 dark:bg-blue-900 dark:text-blue-400'
                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          )}

          {/* Add/Edit Modal */}
          {isModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                <h2 className="text-xl font-bold mb-6 text-gray-900 dark:text-white">
                  {editingRecord ? 'Edit Record' : 'Add New Record'}
                </h2>
                <form onSubmit={handleSubmit}>
                  {masterName === 'CourseMaster' ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Course Name *
                          </label>
                          <input
                            type="text"
                            value={formData.courseName}
                            onChange={(e) => setFormData({ ...formData, courseName: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Status
                          </label>
                          <select
                            value={formData.active ? 'Active' : 'Inactive'}
                            onChange={(e) => setFormData({ ...formData, active: e.target.value === 'Active' })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          >
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Category *
                          </label>
                          <input
                            type="text"
                            value={formData.category}
                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Duration
                          </label>
                          <input
                            type="text"
                            value={formData.duration}
                            onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="e.g., 3 Months"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Price (INR)
                          </label>
                          <input
                            type="number"
                            value={formData.priceINR}
                            onChange={(e) => setFormData({ ...formData, priceINR: parseFloat(e.target.value) || 0 })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Price (USD)
                          </label>
                          <input
                            type="number"
                            value={formData.priceUSD}
                            onChange={(e) => setFormData({ ...formData, priceUSD: parseFloat(e.target.value) || 0 })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Half Payment (INR)
                          </label>
                          <input
                            type="number"
                            value={formData.halfINR}
                            onChange={(e) => setFormData({ ...formData, halfINR: parseFloat(e.target.value) || 0 })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Half Payment (USD)
                          </label>
                          <input
                            type="number"
                            value={formData.halfUSD}
                            onChange={(e) => setFormData({ ...formData, halfUSD: parseFloat(e.target.value) || 0 })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Image URL
                          </label>
                          <input
                            type="text"
                            value={formData.imageUrl}
                            onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="https://example.com/image.jpg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Enrolled Count
                          </label>
                          <input
                            type="number"
                            value={formData.enrolledCount}
                            onChange={(e) => setFormData({ ...formData, enrolledCount: parseInt(e.target.value) || 0 })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          />
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Description *
                        </label>
                        <textarea
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          rows="3"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          required
                        />
                      </div>

                      {formData.imageUrl && (
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Image Preview
                          </label>
                          <img
                            src={formData.imageUrl}
                            alt="Course preview"
                            className="max-w-full h-auto rounded-md border border-gray-300 dark:border-gray-600"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Available';
                            }}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Name *
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          required
                        />
                      </div>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Description *
                        </label>
                        <textarea
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          rows="3"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          required
                        />
                      </div>
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Status
                        </label>
                        <select
                          value={formData.status}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                      </div>
                    </>
                  )}
                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 dark:bg-gray-600 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                    >
                      {editingRecord ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* View Modal */}
          {isViewModalOpen && viewingRecord && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {masterName === 'CourseMaster' ? 'Course Details' : 'Record Details'}
                  </h2>
                  <button
                    onClick={() => setIsViewModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    ✕
                  </button>
                </div>

                {masterName === 'CourseMaster' ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Course ID
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md">
                          {viewingRecord.courseId}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Course Name
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md">
                          {viewingRecord.courseName}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Category
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md">
                          {viewingRecord.category}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Status
                        </label>
                        <p className={`text-sm px-3 py-2 rounded-md inline-block ${
                          viewingRecord.active
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                        }`}>
                          {viewingRecord.active ? 'Active' : 'Inactive'}
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md min-h-[60px]">
                        {viewingRecord.description}
                      </p>
                    </div>

                    {viewingRecord.duration && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Duration
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md">
                          {viewingRecord.duration}
                        </p>
                      </div>
                    )}

                    {(viewingRecord.priceINR > 0 || viewingRecord.priceUSD > 0) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {viewingRecord.priceINR > 0 && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Price (INR)
                            </label>
                            <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md">
                              ₹{viewingRecord.priceINR}
                            </p>
                          </div>
                        )}
                        {viewingRecord.priceUSD > 0 && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Price (USD)
                            </label>
                            <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md">
                              ${viewingRecord.priceUSD}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {(viewingRecord.halfINR > 0 || viewingRecord.halfUSD > 0) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {viewingRecord.halfINR > 0 && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Half Payment (INR)
                            </label>
                            <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md">
                              ₹{viewingRecord.halfINR}
                            </p>
                          </div>
                        )}
                        {viewingRecord.halfUSD > 0 && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Half Payment (USD)
                            </label>
                            <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md">
                              ${viewingRecord.halfUSD}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {viewingRecord.imageUrl && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Image URL
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md break-all">
                          {viewingRecord.imageUrl}
                        </p>
                        <img
                          src={viewingRecord.imageUrl}
                          alt={viewingRecord.courseName}
                          className="mt-2 max-w-full h-auto rounded-md border border-gray-300 dark:border-gray-600"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Available';
                          }}
                        />
                      </div>
                    )}

                    {viewingRecord.enrolledCount !== undefined && viewingRecord.enrolledCount > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Enrolled Count
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md">
                          {viewingRecord.enrolledCount}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          ID
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md">
                          {viewingRecord.id}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Name
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md">
                          {viewingRecord.name}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Status
                        </label>
                        <p className={`text-sm px-3 py-2 rounded-md inline-block ${getStatusBadge(viewingRecord.status)}`}>
                          {viewingRecord.status}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Created Date
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md">
                          {viewingRecord.createdAt}
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md min-h-[60px]">
                        {viewingRecord.description}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setIsViewModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-white bg-gray-600 border border-transparent rounded-md hover:bg-gray-700"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MasterDetail;